// PrepararImagenesTab.js — P3-8 «Preparar imágenes». Sube una o varias fotos, elige el
// USO (preset: hero escritorio/móvil, categoría, banner), ajusta el encuadre con un
// editor de acercar/alejar + arrastrar (como el del producto), y Netto las recorta al
// tamaño del preset y las comprime a JPG con la tubería del navegador. Nombre SEO y alt
// editables. Salida: descargar (una o en .zip) o enviar a la mediateca de la tienda.
// NO toca lo ya subido ni el Hero de Vitrina — eso se elige en wp-admin.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Download, UploadCloud, Trash2, Image as ImageIcon } from 'lucide-react';
import { PRESETS_IMAGEN, presetPorKey, recortePreset, dimsSalida } from '../utils/imagenPresets';
import { chooseQuality } from '../utils/productImages';
import { slugProducto, uploadMedia, descargarImagenPreparada, descargarImagenesZip } from '../utils/productApi';
import { useToast } from './ToastManager';
import './PrepararImagenesTab.css';

const PREVIEW_W = 360;
const bytesDe = (dataUrl) => Math.round((dataUrl.length - dataUrl.indexOf(',') - 1) * 3 / 4);
const kb = (b) => Math.round(b / 1024);
const nombreSeo = (it) => `${slugProducto(it.seo) || 'imagen'}.jpg`;
let _seq = 0;

// Dibuja el recorte final del preset en un canvas al tamaño de SALIDA y devuelve el JPG.
function renderJpg(img, preset, { zoom, cx, cy, ancho }) {
  const srcW = img.naturalWidth; const srcH = img.naturalHeight;
  const { sx, sy, sw, sh } = recortePreset(srcW, srcH, { aspectW: preset.aspectW, aspectH: preset.aspectH, zoom, cx, cy });
  const { outW, outH } = dimsSalida(preset, srcW, srcH, ancho);
  const canvas = document.createElement('canvas');
  canvas.width = outW; canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, outW, outH);
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);
  const q = chooseQuality((qq) => bytesDe(canvas.toDataURL('image/jpeg', qq)), preset.maxKB * 1024);
  const dataUrl = canvas.toDataURL('image/jpeg', q);
  return { dataUrl, outW, outH, bytes: bytesDe(dataUrl) };
}

// Editor de UN ítem: vista previa del recorte (arrastrar para mover) + zoom.
function EditorEncuadre({ item, onChange }) {
  const canvasRef = useRef(null);
  const drag = useRef(null);
  const preset = presetPorKey(item.presetKey);
  const ar = preset.aspectW && preset.aspectH ? preset.aspectW / preset.aspectH
    : (item.img ? item.img.naturalWidth / item.img.naturalHeight : 1);
  const previewH = Math.round(PREVIEW_W / ar);

  useEffect(() => {
    const c = canvasRef.current; const img = item.img;
    if (!c || !img) return;
    c.width = PREVIEW_W; c.height = previewH;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const { sx, sy, sw, sh } = recortePreset(img.naturalWidth, img.naturalHeight,
      { aspectW: preset.aspectW, aspectH: preset.aspectH, zoom: item.zoom, cx: item.cx, cy: item.cy });
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, PREVIEW_W, previewH);
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, PREVIEW_W, previewH);
  }, [item.img, item.zoom, item.cx, item.cy, item.presetKey, preset.aspectW, preset.aspectH, previewH]);

  const alMover = (e) => {
    if (!drag.current) return;
    const dx = (e.clientX - drag.current.x) / PREVIEW_W;
    const dy = (e.clientY - drag.current.y) / previewH;
    // Arrastrar la imagen a la derecha = mostrar lo de la izquierda → cx baja.
    onChange({ cx: Math.min(1, Math.max(0, drag.current.cx - dx)), cy: Math.min(1, Math.max(0, drag.current.cy - dy)) });
  };
  const soltar = () => { drag.current = null; };

  return (
    <div className="prep-editor">
      <canvas
        ref={canvasRef}
        className="prep-canvas"
        style={{ width: PREVIEW_W, height: previewH, cursor: item.zoom > 1 ? 'grab' : 'default' }}
        onMouseDown={(e) => { drag.current = { x: e.clientX, y: e.clientY, cx: item.cx, cy: item.cy }; }}
        onMouseMove={alMover}
        onMouseUp={soltar}
        onMouseLeave={soltar}
      />
      <label className="prep-zoom">
        Acercar
        <input type="range" min="1" max="4" step="0.05" value={item.zoom}
          onChange={(e) => onChange({ zoom: Number(e.target.value) })} />
      </label>
    </div>
  );
}

function ItemPreparacion({ item, onChange, onQuitar, onDescargar, onEnviar, soloLectura }) {
  const preset = presetPorKey(item.presetKey);
  const anchos = preset.anchos;
  return (
    <div className="prep-item">
      <div className="prep-item-top">
        <EditorEncuadre item={item} onChange={onChange} />
        <div className="prep-item-form">
          <label className="prep-campo">
            Uso
            <select value={item.presetKey} onChange={(e) => onChange({ presetKey: e.target.value })}>
              {PRESETS_IMAGEN.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
            <span className="prep-ayuda">{preset.ayuda}</span>
          </label>

          {anchos && (
            <label className="prep-campo">
              Ancho
              <select value={item.ancho || preset.anchoDefecto} onChange={(e) => onChange({ ancho: Number(e.target.value) })}>
                {anchos.map((a) => <option key={a} value={a}>{a} px</option>)}
              </select>
            </label>
          )}
          {preset.anchoLibre && (
            <label className="prep-campo">
              Ancho (px)
              <input type="number" min="320" max="4000" step="20" value={item.ancho || preset.anchoDefecto}
                onChange={(e) => onChange({ ancho: Math.min(4000, Math.max(320, Number(e.target.value) || preset.anchoDefecto)) })} />
            </label>
          )}

          <label className="prep-campo">
            Nombre (SEO)
            <input type="text" value={item.seo} placeholder="nombre-del-archivo"
              onChange={(e) => onChange({ seo: e.target.value })} />
          </label>
          <label className="prep-campo">
            Texto alternativo
            <input type="text" value={item.alt} placeholder="Describe la imagen"
              onChange={(e) => onChange({ alt: e.target.value })} />
          </label>

          <div className="prep-salida">
            {item.out
              ? <span>{item.out.outW}×{item.out.outH} · {kb(item.out.bytes)} KB · JPG</span>
              : <span>calculando…</span>}
          </div>
        </div>
      </div>
      <div className="prep-item-acciones">
        <button type="button" className="prep-btn" onClick={() => onDescargar(item)} disabled={!item.out}>
          <Download size={14} /> Descargar
        </button>
        {!soloLectura && (
          <button type="button" className="prep-btn" onClick={() => onEnviar(item)} disabled={!item.out || item.enviando}>
            <UploadCloud size={14} /> {item.enviando ? 'Enviando…' : 'Enviar a la mediateca'}
          </button>
        )}
        <button type="button" className="prep-btn prep-btn--danger" onClick={() => onQuitar(item.key)} title="Quitar">
          <Trash2 size={14} />
        </button>
        {item.enviado && <span className="prep-enviado">✓ en la mediateca</span>}
      </div>
    </div>
  );
}

export default function PrepararImagenesTab({ soloLectura = false }) {
  const [items, setItems] = useState([]);
  const [zipLoading, setZipLoading] = useState(false);
  const inputRef = useRef(null);
  const { showToast } = useToast();

  const patch = useCallback((key, campos) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...campos } : it)));
  }, []);

  // Recalcula el JPG de salida cuando cambian preset/ancho/encuadre.
  useEffect(() => {
    let cancel = false;
    const pend = items.filter((it) => it.img && it.dirty);
    if (!pend.length) return undefined;
    const id = setTimeout(() => {
      if (cancel) return;
      setItems((prev) => prev.map((it) => {
        if (!it.img || !it.dirty) return it;
        const out = renderJpg(it.img, presetPorKey(it.presetKey), { zoom: it.zoom, cx: it.cx, cy: it.cy, ancho: it.ancho });
        return { ...it, out, dirty: false };
      }));
    }, 250);
    return () => { cancel = true; clearTimeout(id); };
  }, [items]);

  const marcarSucio = useCallback((key, campos) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...campos, dirty: true, enviado: false } : it)));
  }, []);

  const agregarArchivos = useCallback((fileList) => {
    const files = [...(fileList || [])].filter((f) => f.type.startsWith('image/'));
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          _seq += 1;
          const base = slugProducto(file.name.replace(/\.[^.]+$/, '')) || 'imagen';
          setItems((prev) => [...prev, {
            key: `p${_seq}`, img, dataUrl: reader.result, nombreArchivo: file.name,
            presetKey: 'hero-escritorio', zoom: 1, cx: 0.5, cy: 0.5, ancho: null,
            seo: base, alt: '', out: null, dirty: true, enviado: false, enviando: false,
          }]);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const quitar = useCallback((key) => setItems((prev) => prev.filter((it) => it.key !== key)), []);

  const descargar = useCallback((it) => {
    if (!it.out) return;
    descargarImagenPreparada(it.out.dataUrl, nombreSeo(it));
  }, []);

  const enviar = useCallback(async (it) => {
    if (!it.out) return;
    patch(it.key, { enviando: true });
    try {
      await uploadMedia({ imageBase64: it.out.dataUrl.split(',')[1], imageMime: 'image/jpeg', filename: nombreSeo(it), altText: it.alt || '', contexto: 'preparacion' });
      patch(it.key, { enviando: false, enviado: true });
      showToast({ message: 'Imagen enviada a la mediateca de la tienda.', variant: 'success' });
    } catch (e) {
      patch(it.key, { enviando: false });
      showToast({ message: `No se pudo enviar: ${e.message}`, variant: 'error' });
    }
  }, [patch, showToast]);

  const descargarZip = useCallback(async () => {
    const listos = items.filter((it) => it.out);
    if (!listos.length) return;
    setZipLoading(true);
    try {
      await descargarImagenesZip(listos.map((it) => ({ nombre: nombreSeo(it), base64: it.out.dataUrl })));
    } catch (e) {
      showToast({ message: `No se pudo armar el zip: ${e.message}`, variant: 'error' });
    } finally { setZipLoading(false); }
  }, [items, showToast]);

  const listos = items.filter((it) => it.out).length;

  return (
    <div className="prep-tab">
      <div className="prep-intro">
        <p>Prepara imágenes para la tienda (hero, banner, categoría): se recortan al tamaño del uso elegido y se comprimen a JPG. Después las descargas o las envías a la mediateca; <strong>colocarlas en el Hero se sigue haciendo en wp-admin</strong>.</p>
      </div>

      <div className="prep-subir">
        <button type="button" className="prep-btn prep-btn--primary" onClick={() => inputRef.current && inputRef.current.click()}>
          <ImageIcon size={15} /> Elige imágenes
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple hidden
          onChange={(e) => { agregarArchivos(e.target.files); e.target.value = ''; }} />
        {items.length > 0 && (
          <button type="button" className="prep-btn" onClick={descargarZip} disabled={!listos || zipLoading}>
            <Download size={14} /> {zipLoading ? 'Armando…' : `Descargar ${listos} en .zip`}
          </button>
        )}
      </div>

      {items.length === 0
        ? <div className="prep-vacio">Aún no agregaste imágenes. Elige una o varias para empezar.</div>
        : (
          <div className="prep-lista">
            {items.map((it) => (
              <ItemPreparacion key={it.key} item={it} soloLectura={soloLectura}
                onChange={(campos) => marcarSucio(it.key, campos)}
                onQuitar={quitar} onDescargar={descargar} onEnviar={enviar} />
            ))}
          </div>
        )}
    </div>
  );
}
